import { redirect } from '@tanstack/react-router';
import { isCancelledError, type QueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { surviveCancel } from '@/api/loaders';
import { ApiError } from '@/api/errors';
import { sessionMe } from '@/api/resources';
import { copilotStatusQueryOptions } from '@/api/hooks';
import { canAtLeast } from '@/auth/capabilities';
import { parseCreativeId, type ConnectSearch } from '@/lib/oauth-consent';
import type { Capability, CapabilityLevel, User } from '@/lib/types';

interface RouteLoadOptions {
  context: { queryClient: QueryClient };
}

/** beforeLoad guard for all authenticated routes — 401 → /login. */
export async function requireSession({ context }: RouteLoadOptions) {
  try {
    await surviveCancel(context.queryClient, sessionMe.key(undefined), () =>
      context.queryClient.fetchQuery(sessionMe(undefined)),
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      throw redirect({ to: '/login' });
    }
    throw err;
  }
}

/**
 * beforeLoad for `/connect`, the consent page: a session, or `/login` carrying the way back
 * (`?next=connect&request=<id>`, lib/oauth-consent.ts), so a person who signs in there returns to
 * the request Claude is waiting on. It also seeds the session the page shows ("signed in as").
 * The page is on a public path (no chrome, no revision manifest), so this is its only guard.
 */
export async function requireConsentSession({
  context,
  search,
}: RouteLoadOptions & { search: ConnectSearch }) {
  try {
    await surviveCancel(context.queryClient, sessionMe.key(undefined), () =>
      context.queryClient.fetchQuery(sessionMe(undefined)),
    );
  } catch (err) {
    // A 401 reaches here as itself, or as the cancel of this very read: the 401 handler purges
    // the private cache before the request's promise settles, and the purge cancels the `/me`
    // it removes (`surviveCancel` never re-asks a removed query). Either way there is no session
    // to show, and the page must not land on the router's error panel.
    if ((err instanceof ApiError && err.status === 401) || isCancelledError(err)) {
      throw search.request
        ? redirect({ to: '/login', search: { next: 'connect', request: search.request } })
        : redirect({ to: '/login' });
    }
    throw err;
  }
}

/**
 * beforeLoad for `/creatives/$creativeId/assets`, the page Claude links a person to for a
 * creative's files: a session, or `/login` carrying the way back
 * (`?next=creative-assets&creative=<id>`), so the link survives a sign-in. The route then asks
 * `requireCapability('creatives_edit')`, which finds the session already read.
 */
export async function requireCreativeAssetsSession({
  context,
  params,
}: RouteLoadOptions & { params: { creativeId: string } }) {
  try {
    await surviveCancel(context.queryClient, sessionMe.key(undefined), () =>
      context.queryClient.fetchQuery(sessionMe(undefined)),
    );
  } catch (err) {
    // As `requireConsentSession`: a 401 arrives as itself or as the cancel of this read.
    if ((err instanceof ApiError && err.status === 401) || isCancelledError(err)) {
      const creative = parseCreativeId(params.creativeId);
      throw creative
        ? redirect({ to: '/login', search: { next: 'creative-assets', creative } })
        : redirect({ to: '/login' });
    }
    throw err;
  }
}

/**
 * Authenticated route guard for capability-scoped screens. The API remains the
 * security boundary; this prevents direct URLs from exposing write-only Manage
 * flows to roles whose sidebar correctly hides them.
 */
export function requireCapability(
  capability: Capability,
  minimum: CapabilityLevel = 'view',
) {
  return async (options: RouteLoadOptions) => {
    await requireSession(options);
    const user = options.context.queryClient.getQueryData<User>(sessionMe.key(undefined));
    if (!user || !canAtLeast(user.role, capability, minimum)) {
      throw redirect({ to: '/' });
    }
  };
}

/**
 * As `requireCapability`, plus "must be an unscoped admin". Some admin routes
 * (Config) are rejected by the API for any user with a production scope even
 * at `full` capability, so the guard has to match or the screen is a dead end
 * that loads and then 403s on its first fetch.
 */
export function requireGlobalCapability(
  capability: Capability,
  minimum: CapabilityLevel = 'view',
) {
  return async (options: RouteLoadOptions) => {
    await requireSession(options);
    const user = options.context.queryClient.getQueryData<User>(sessionMe.key(undefined));
    if (!user || !canAtLeast(user.role, capability, minimum) || user.productions != null) {
      throw redirect({ to: '/' });
    }
  };
}

/**
 * Resolve the copilot flag for the '/' landing decision — same query options
 * as useCopilotStatus so the answer seeds (or reads) the shared cache slot.
 * The homepage must never hang on the copilot endpoint: a slow response
 * (past `timeoutMs`) or any error resolves false and the caller falls back
 * to the productions screen. The fetch keeps running in the background, so
 * the sidebar's Morning Brief section still appears once it lands.
 */
export async function copilotEnabled(
  { context }: { context: { queryClient: QueryClient } },
  timeoutMs = 1500,
): Promise<boolean> {
  try {
    const status = await Promise.race([
      context.queryClient.fetchQuery(copilotStatusQueryOptions),
      new Promise<null>((resolve) => { setTimeout(() => resolve(null), timeoutMs); }),
    ]);
    return status?.enabled === true;
  } catch {
    return false;
  }
}
